import React, { FunctionComponent, ReactElement, useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { Dimension } from '@/interfaces';
import { SetProduct } from '@/src/components/ProductSelection/types';
import Card from '@/src/components/Card';
import { PickerTile } from './tile';
import { SubDimensionWrapper, SubDimensionName } from './styles';
import { PickerTilesLayoutProps } from './types';

const TilesHeadline = styled.div`
  font-weight: bold;
  font-size: 1rem;
  margin: 0.5rem 1rem;
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;
`;

const MobilePickerWrapper = styled.div<{ noPaddingTop: boolean }>`
  ${({ noPaddingTop }) => !noPaddingTop && 'padding: 1rem 0;'}
`;

const PickerTilesContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  flex-flow: row wrap;
  padding: 0 1rem;
`;

const TrackWrapper = styled.div<{
  trackTranslate: number;
  trackWidth: number;
  isExpanded: boolean;
}>`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  flex-flow: ${({ isExpanded }) => (isExpanded ? 'row wrap' : 'row nowrap')};
  position: relative;
  will-change: transform;
  touch-action: pan-y;
  .picker-tile-value {
    white-space: ${({ isExpanded }) => (isExpanded ? 'normal' : 'nowrap')};
  }
`;

const PickerTilesTrackWrapper = styled(TrackWrapper).attrs<{
  trackTranslate: number;
  trackWidth: number;
  isExpanded: boolean;
}>(({ trackTranslate, trackWidth, isExpanded }) => ({
  style: {
    transform: isExpanded
      ? 'translate3d(0px, 0px, 0px)'
      : `translate3d(${trackTranslate}px, 0px, 0px)`,
    transition: isExpanded
      ? ''
      : `transform ${Math.sqrt(1.5 * trackWidth) * 5}ms cubic-bezier(0.22, 0.36, 0.57, 1)`,
  },
}))``;

const PickerTilesTrack = styled.div<{ trackWidth: number; isExpanded: boolean }>`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  flex-flow: ${({ isExpanded }) => (isExpanded ? 'row wrap' : 'row nowrap')};
  position: relative;
  width: ${({ trackWidth, isExpanded }) => (isExpanded ? '100%' : `${trackWidth}px;`)};
`;

const ExpandSlider = styled.div`
  margin-left: auto;
  font-size: 0.75rem;
  color: #ef7c00;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
`;

interface MobilePickerProps {
  dimension: Dimension;
  setProduct: SetProduct;
  setIsExpandable: (isExpandable: boolean) => void;
  isExpanded: boolean;
  axisType: string;
}

const MobilePicker: FunctionComponent<MobilePickerProps> = ({
  dimension,
  setProduct,
  setIsExpandable,
  isExpanded,
  axisType,
}): ReactElement => {
  const [trackWidth, setTrackWidth] = useState<number>();
  const [trackHeight, setTrackHeight] = useState<number>();
  const [touchStart, setTouchStart] = useState<number>(0);
  const [trackTranslate, setTrackTranslate] = useState<number>(0);
  const [isSwipeEnabled, setIsSwipeEnabled] = useState<boolean>(false);
  const track = useRef(null);
  const container = useRef(null);

  useEffect(() => {
    if (track?.current?.offsetWidth > container?.current?.offsetWidth - 32) {
      setIsSwipeEnabled(true);
      setTrackHeight(track.current.clientHeight);
      setTrackWidth(track.current.offsetWidth);
      if (dimension.values.length > 1) {
        setIsExpandable(true);
      }
    }
  }, [track?.current?.offsetWidth, container?.current?.offsetWidth]);

  useEffect(() => {
    setTrackHeight(track.current.clientHeight);
    setTrackWidth(track.current.offsetWidth);
  }, [isExpanded]);

  return (
    <PickerTilesContainer ref={container}>
      <div
        style={{
          height: `${trackHeight}px`,
          transition: `height ${
            Math.sqrt(1.5 * trackHeight) * 5
          }ms cubic-bezier(0.22, 0.36, 0.57, 1)`,
          willChange: 'height',
        }}
      >
        <PickerTilesTrack
          isExpanded={isExpanded}
          trackWidth={trackWidth + 1}
          onTouchStart={(event) => {
            if (isSwipeEnabled) {
              setTouchStart(event.changedTouches[0].clientX);
            }
          }}
          onTouchMove={(event) => {
            if (isSwipeEnabled) {
              let newTranslate = trackTranslate + (event.changedTouches[0].clientX - touchStart);
              if (newTranslate > 0) {
                newTranslate = 0;
              } else if (newTranslate * -1 > trackWidth - (container.current.offsetWidth - 32)) {
                newTranslate = (trackWidth - (container.current.offsetWidth - 32)) * -1;
              }
              setTrackTranslate(newTranslate);
              setTouchStart(event.changedTouches[0].clientX);
            }
          }}
          onTouchEnd={() => {
            if (isSwipeEnabled) {
              setTouchStart(trackTranslate);
            }
          }}
        >
          <PickerTilesTrackWrapper
            ref={track}
            trackTranslate={trackTranslate}
            trackWidth={trackWidth}
            isExpanded={isExpanded}
          >
            {dimension.values.map((value) => (
              <PickerTile
                value={value}
                setProduct={setProduct}
                axisType={axisType}
                key={value.displayText}
                availability={value.availability}
                inputType={dimension.type}
              />
            ))}
          </PickerTilesTrackWrapper>
        </PickerTilesTrack>
      </div>
    </PickerTilesContainer>
  );
};

export const MobilePickerTiles: FunctionComponent<PickerTilesLayoutProps> = ({
  dimension,
  setProduct,
}) => {
  const [isExpandable, setIsExpandable] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const subDimensions = dimension.values.filter((dim) => dim.subDimension !== undefined);

  return (
    <Card
      headline={
        <TilesHeadline>
          {dimension.displayName && dimension.displayName !== '' ? (
            dimension.displayName
          ) : (
            <FormattedMessage id="dimension-name.fallback" defaultMessage="Variante" />
          )}
          {isExpandable && (
            <ExpandSlider onClick={() => setIsExpanded(!isExpanded)}>
              {isExpanded ? (
                <FormattedMessage
                  id="dimension-name.shrink-slider"
                  defaultMessage="weniger anzeigen"
                />
              ) : (
                <FormattedMessage
                  id="dimension-name.expand-slider"
                  defaultMessage="alle anzeigen"
                />
              )}
            </ExpandSlider>
          )}
        </TilesHeadline>
      }
    >
      {subDimensions.length > 0 ? (
        subDimensions.map((subDimension, index) => (
          <SubDimensionWrapper
            data-testid="mobile-subdimension-wrapper"
            key={subDimension.displayText.concat(index.toFixed())}
          >
            <SubDimensionName data-testid="mobile-subdimension-name" isMobile>
              {subDimension.displayText}
            </SubDimensionName>
            <MobilePickerWrapper data-testid="mobile-picker" noPaddingTop>
              <MobilePicker
                dimension={subDimension.subDimension}
                setProduct={setProduct}
                setIsExpandable={setIsExpandable}
                isExpanded={isExpanded}
                axisType={dimension.axisType}
              />
            </MobilePickerWrapper>
          </SubDimensionWrapper>
        ))
      ) : (
        <MobilePickerWrapper data-testid="mobile-picker" noPaddingTop={false}>
          <MobilePicker
            dimension={dimension}
            setProduct={setProduct}
            setIsExpandable={setIsExpandable}
            isExpanded={isExpanded}
            axisType={dimension.axisType}
          />
        </MobilePickerWrapper>
      )}
    </Card>
  );
};

export default MobilePickerTiles;
